import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import type { Bill } from '../../../types/bookkeeping.js';

const STATUS_CLASSES: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700',
  partial: 'bg-amber-100 text-amber-700',
  paid: 'bg-green-100 text-green-700',
  void: 'bg-slate-100 text-slate-500',
};

export function Bills() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['bk-bills', activeOrgId],
    queryFn: () => api<{ bills: Bill[] }>('/bookkeeping/purchases/bills?limit=50'),
    enabled: !!activeOrgId,
  });

  const bills = data?.bills ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('purchases.bills.title')}</h1>
        <Link
          to="/app/bookkeeping/purchases/bills/new"
          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          + {t('purchases.bills.new')}
        </Link>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common:loading')}</p>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">{t('purchases.bills.fields.number')}</th>
                <th className="px-3 py-2">{t('purchases.bills.fields.supplier_number')}</th>
                <th className="px-3 py-2">{t('purchases.bills.fields.vendor')}</th>
                <th className="px-3 py-2">{t('purchases.bills.fields.entry_date')}</th>
                <th className="px-3 py-2">{t('purchases.bills.fields.due_date')}</th>
                <th className="px-3 py-2">{t('purchases.bills.fields.currency')}</th>
                <th className="px-3 py-2 text-right">{t('purchases.bills.fields.grand_total')}</th>
                <th className="px-3 py-2 text-right">{t('purchases.bills.fields.paid_amount')}</th>
                <th className="px-3 py-2">{t('purchases.bills.fields.status')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {bills.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-4 text-center text-slate-400">
                    {t('purchases.bills.empty', {
                      defaultValue: 'No hay facturas de proveedor aún.',
                    })}
                  </td>
                </tr>
              )}
              {bills.map((bill) => (
                <tr key={bill.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link
                      to={`/app/bookkeeping/purchases/bills/${bill.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {bill.number}
                    </Link>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{bill.supplier_number}</td>
                  <td className="px-3 py-2">{bill.vendor_id}</td>
                  <td className="px-3 py-2">{bill.entry_date}</td>
                  <td className="px-3 py-2">{bill.due_date}</td>
                  <td className="px-3 py-2">{bill.currency}</td>
                  <td className="px-3 py-2 text-right font-mono">{bill.grand_total}</td>
                  <td className="px-3 py-2 text-right font-mono">{bill.paid_amount}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-sm px-1.5 py-0.5 text-xs ${STATUS_CLASSES[bill.status] ?? ''}`}
                    >
                      {t(`purchases.bills.statuses.${bill.status}`, { defaultValue: bill.status })}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`/app/bookkeeping/purchases/bills/${bill.id}`}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      {t('journalEntries.viewEdit')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
